import { useEffect, useState } from "react";
import { Plus, Play, Check, Ban, Archive, RotateCcw, Trash2, Send, ExternalLink, Target, Search, X, AlertTriangle } from "lucide-react";
import {
  api, formatApiError, SPRINT_STATUS_LABELS, SPRINT_STATUS_STYLE,
  CLASS_STYLE, PRIO_STYLE, PRIORITY_LABELS,
} from "@/lib/api";
import { toast } from "sonner";
import ColumnPicker from "@/components/ColumnPicker";

const PALETTE = ["#2563eb", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#db2777", "#ca8a04", "#4f46e5", "#0d9488", "#dc2626"];
const emptySprint = { name: "", goal: "", start_date: "", end_date: "", color: "" };

const DECISION_OPTIONS = [
  { value: "backlog", label: "Devolver ao Backlog" },
  { value: "keep_no_sprint", label: "Manter sem Sprint" },
  { value: "archive", label: "Arquivar" },
  { value: "cancel", label: "Marcar como cancelada" },
];

const taskLocation = (t) => {
  if (t.impediment_active) return "Impedimento/Problema";
  if (t.on_board) return `Kanban — ${t.board_column_name}`;
  return "Backlog";
};

export default function Sprints({ projectId, columns, refreshKey, onOpenTask, onChanged, onGoKanban }) {
  const [data, setData] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [completeFor, setCompleteFor] = useState(null);
  const [picker, setPicker] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/sprints`, { params: showArchived ? { include_archived: "true" } : {} }); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, showArchived, refreshKey]);

  const canAdmin = data?.can_admin;
  const sprints = data?.sprints || [];

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, goal: form.goal, start_date: form.start_date || null, end_date: form.end_date || null };
      if (form.color) payload.color = form.color;
      if (form.id) { await api.patch(`/sprints/${form.id}`, payload); toast.success("Sprint atualizada."); }
      else { await api.post(`/projects/${projectId}/sprints`, payload); toast.success("Sprint criada."); }
      setForm(null); load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const act = async (id, action) => {
    try { await api.post(`/sprints/${id}/${action}`); toast.success("Atualizado."); load(); onChanged?.(); if (detail?.id === id) openDetail(id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeSprint = async (id) => {
    if (!window.confirm("Excluir esta sprint?")) return;
    try { await api.delete(`/sprints/${id}`); toast.success("Sprint excluída."); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doCancel = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/sprints/${cancelFor.id}/cancel`, { reason: cancelFor.reason, move_incomplete_to: cancelFor.dest });
      toast.success("Sprint cancelada."); setCancelFor(null); load(); onChanged?.(); if (detail?.id === cancelFor.id) setDetail(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/sprints/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendToKanban = async (columnId) => {
    const ids = picker.ids;
    try {
      if (picker.single) await api.post(`/tasks/${ids[0]}/send-to-kanban`, { column_id: columnId });
      else await api.post(`/projects/${projectId}/tasks/send-to-kanban`, { task_ids: ids, column_id: columnId });
      toast.success("Enviada(s) ao Kanban."); setPicker(null); openDetail(detail.id); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeFromSprint = async (taskId) => {
    try { await api.delete(`/sprints/${detail.id}/tasks/${taskId}`); toast.success("Tarefa removida da sprint."); openDetail(detail.id); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="sprints-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="sprints-show-archived" /> Mostrar arquivadas
        </label>
        {canAdmin && <button onClick={() => setForm({ ...emptySprint })} data-testid="create-sprint-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"><Plus className="w-4 h-4" /> Nova sprint</button>}
      </div>

      {!data ? <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>
        : sprints.length === 0 ? <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-500" data-testid="sprints-empty">Nenhuma sprint.</div>
          : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sprints-grid">
              {sprints.map((s) => (
                <div key={s.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md hover:border-slate-300 transition-all" data-testid={`sprint-card-${s.id}`}>
                  <div className="h-1.5" style={{ backgroundColor: s.color || "#64748b" }} />
                  <div className="p-4">
                    <div
                      role="button" tabIndex={0}
                      onClick={() => openDetail(s.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(s.id); } }}
                      data-testid={`sprint-open-${s.id}`}
                      className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 rounded-md">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-heading font-semibold text-slate-900 truncate min-w-0">{s.name}</div>
                        <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${SPRINT_STATUS_STYLE[s.status]}`}>{SPRINT_STATUS_LABELS[s.status]}</span>
                      </div>
                      {s.goal
                        ? <p className="text-xs text-slate-500 mt-1 line-clamp-2 flex items-start gap-1"><Target className="w-3 h-3 mt-0.5 shrink-0" />{s.goal}</p>
                        : <p className="text-xs text-slate-300 mt-1 italic">Sem objetivo definido</p>}
                      <div className="text-xs text-slate-400 mt-2">{s.start_date || "—"} → {s.end_date || "—"}</div>

                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-slate-600 font-medium">{s.done_count ?? 0} de {s.tasks_count ?? 0} tarefas concluídas</span>
                          <span className="text-slate-900 font-semibold">{s.progress ?? 0}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden" data-testid={`sprint-progress-${s.id}`}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${s.progress ?? 0}%`, backgroundColor: s.color || "#10b981" }} />
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1" data-testid={`sprint-dist-${s.id}`}>
                          {["E", "D", "O", "Q"].map((k) => (
                            <span key={k} className={`inline-flex items-center justify-center min-w-[2.25rem] h-6 px-1 rounded-md text-[11px] font-semibold ${CLASS_STYLE[k]}`} title={k}>
                              {k} {(s.distribution || {})[k] ?? 0}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-slate-500 shrink-0">{s.story_points ?? 0} SP</span>
                      </div>
                      {s.status === "cancelled" && s.cancel_reason && <div className="text-xs text-rose-600 mt-2">Cancelada: {s.cancel_reason}</div>}
                    </div>
                    {canAdmin && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {s.status === "planned" && <ActBtn testid={`sprint-start-${s.id}`} onClick={() => act(s.id, "start")} icon={Play} label="Iniciar" />}
                        {s.status === "active" && <ActBtn testid={`sprint-complete-${s.id}`} onClick={() => setCompleteFor({ id: s.id })} icon={Check} label="Concluir" />}
                        {(s.status === "active" || s.status === "planned") && <ActBtn testid={`sprint-cancel-${s.id}`} onClick={() => setCancelFor({ id: s.id, reason: "", dest: "backlog" })} icon={Ban} label="Cancelar" danger />}
                        {(s.status === "completed" || s.status === "cancelled") && <ActBtn testid={`sprint-archive-${s.id}`} onClick={() => act(s.id, "archive")} icon={Archive} label="Arquivar" />}
                        {s.status === "cancelled" && <ActBtn testid={`sprint-restore-${s.id}`} onClick={() => act(s.id, "restore")} icon={RotateCcw} label="Restaurar" />}
                        {s.status === "planned" && <ActBtn testid={`sprint-edit-${s.id}`} onClick={() => setForm({ id: s.id, name: s.name, goal: s.goal, start_date: s.start_date || "", end_date: s.end_date || "", color: s.color || "" })} label="Editar" />}
                        {s.status === "planned" && !s.started && <ActBtn testid={`sprint-delete-${s.id}`} onClick={() => removeSprint(s.id)} icon={Trash2} label="Excluir" danger />}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

      {/* Create / Edit sprint */}
      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={() => setForm(null)} data-testid="sprint-form-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitForm} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md p-6 space-y-4 my-8">
            <h3 className="font-heading text-xl font-semibold">{form.id ? "Editar sprint" : "Nova sprint"}</h3>
            <input required placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sprint-name" className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm" />
            <textarea rows={2} placeholder="Objetivo da sprint" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="sprint-goal" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="block text-xs text-slate-500 mb-1">Início</span><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="sprint-start-date" className="prop-select" /></label>
              <label className="block"><span className="block text-xs text-slate-500 mb-1">Fim</span><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="sprint-end-date" className="prop-select" /></label>
            </div>
            <div>
              <span className="block text-xs text-slate-500 mb-1">Cor</span>
              <div className="flex flex-wrap gap-2" data-testid="sprint-color-palette">
                {PALETTE.map((c) => (
                  <button type="button" key={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : ""}`} style={{ backgroundColor: c }} aria-label={`Cor ${c}`} />
                ))}
                <span className="text-xs text-slate-400 self-center">{form.color ? "" : "(automática)"}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" data-testid="sprint-submit" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium">Salvar</button>
            </div>
          </form>
        </div>
      )}

      {/* Complete sprint (full closure) */}
      {completeFor && (
        <CompleteSprintModal sprintId={completeFor.id} onClose={() => setCompleteFor(null)}
          onDone={() => { setCompleteFor(null); load(); onChanged?.(); if (detail?.id === completeFor.id) openDetail(completeFor.id); }} />
      )}

      {/* Cancel sprint */}
      {cancelFor && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={() => setCancelFor(null)} data-testid="sprint-cancel-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={doCancel} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-heading text-xl font-semibold">Cancelar sprint</h3>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Motivo (obrigatório)</label>
              <textarea required rows={3} value={cancelFor.reason} onChange={(e) => setCancelFor({ ...cancelFor, reason: e.target.value })} data-testid="cancel-reason" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Destino das tarefas incompletas</label>
              <select value={cancelFor.dest} onChange={(e) => setCancelFor({ ...cancelFor, dest: e.target.value })} data-testid="cancel-dest" className="prop-select">
                <option value="backlog">Voltar ao Backlog (sem sprint)</option>
                {sprints.filter((s) => s.id !== cancelFor.id && ["planned", "active"].includes(s.status)).map((s) => <option key={s.id} value={s.id}>Mover para: {s.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCancelFor(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Voltar</button>
              <button type="submit" data-testid="cancel-confirm" className="h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-medium">Confirmar cancelamento</button>
            </div>
          </form>
        </div>
      )}

      {/* Sprint detail */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={() => setDetail(null)} data-testid="sprint-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-2xl p-6 space-y-4 my-8">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: detail.color }} />
                  <h3 className="font-heading text-xl font-semibold">{detail.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${SPRINT_STATUS_STYLE[detail.status]}`}>{SPRINT_STATUS_LABELS[detail.status]}</span>
                </div>
                {detail.goal && <p className="text-sm text-slate-500 mt-1">{detail.goal}</p>}
              </div>
              {columns.length > 0 && (detail.tasks || []).some((t) => !t.on_board) && (
                <button onClick={() => setPicker({ ids: (detail.tasks || []).filter((t) => !t.on_board).map((t) => t.id) })} data-testid="sprint-send-all" className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-slate-900 text-white text-xs"><Send className="w-3.5 h-3.5" /> Enviar todas ao Kanban</button>
              )}
            </div>

            <div className="border-t border-slate-100 pt-3">
              {(detail.tasks || []).length === 0 ? <p className="text-sm text-slate-400">Nenhuma tarefa associada.</p> : (
                <ul className="divide-y divide-slate-100" data-testid="sprint-tasks">
                  {detail.tasks.map((t) => (
                    <li key={t.id} className="py-2.5 flex items-center gap-3">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onOpenTask(t.id)} data-testid={`sprint-task-${t.id}`}>
                        <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                        <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                          <span className={t.impediment_active ? "text-amber-700" : t.on_board ? "text-emerald-700" : "text-slate-500"}>{taskLocation(t)}</span>
                          {t.assignee_name && <span className="text-slate-400">· {t.assignee_name}</span>}
                          {t.story_points != null && <span className="text-slate-400">· {t.story_points} SP</span>}
                        </div>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${CLASS_STYLE[t.classification]}`}>{t.classification}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${PRIO_STYLE[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
                      {detail.can_admin && ["planned", "active"].includes(detail.status) && (
                        <button onClick={() => removeFromSprint(t.id)} data-testid={`sprint-remove-task-${t.id}`} title="Remover da sprint" className="p-1.5 rounded-md border border-slate-200 hover:bg-rose-50 hover:border-rose-200"><X className="w-4 h-4 text-slate-500" /></button>
                      )}
                      {t.on_board
                        ? <button onClick={onGoKanban} data-testid={`sprint-view-kanban-${t.id}`} title="Ver no Kanban" className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"><ExternalLink className="w-4 h-4 text-slate-600" /></button>
                        : (columns.length > 0 && t.can_edit !== false && <button onClick={() => setPicker({ ids: [t.id], single: true })} data-testid={`sprint-send-${t.id}`} title="Enviar ao Kanban" className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50"><Send className="w-4 h-4 text-slate-600" /></button>)}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Add tasks to sprint (planned/active, admin) */}
            {detail.can_admin && ["planned", "active"].includes(detail.status) && (
              <AddTasksPanel sprintId={detail.id} onChanged={() => { openDetail(detail.id); onChanged?.(); }} />
            )}

            {/* Permanent closure summary for completed sprint */}
            {detail.status === "completed" && detail.closure && <ClosureSummary closure={detail.closure} />}
          </div>
        </div>
      )}

      {picker && <ColumnPicker columns={columns} count={picker.single ? null : picker.ids.length} onPick={sendToKanban} onClose={() => setPicker(null)} />}
    </div>
  );
}

const ActBtn = ({ onClick, icon: Icon, label, danger, testid }) => (
  <button onClick={(e) => { e.stopPropagation(); onClick(e); }} data-testid={testid} className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-xs ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
    {Icon && <Icon className="w-3.5 h-3.5" />} {label}
  </button>
);

// ---- Add tasks to sprint panel ----
function AddTasksPanel({ sprintId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [q, setQ] = useState(""); const [fClass, setFClass] = useState(""); const [fPrio, setFPrio] = useState("");
  const [busy, setBusy] = useState(false);

  const loadEligible = async () => {
    try { const { data } = await api.get(`/sprints/${sprintId}/eligible-tasks`); setEligible(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (open) { loadEligible(); setSel(new Set()); } /* eslint-disable-next-line */ }, [open]);

  const filtered = (eligible || []).filter((t) => {
    if (q && !t.title.toLowerCase().includes(q.trim().toLowerCase())) return false;
    if (fClass && t.classification !== fClass) return false;
    if (fPrio && t.priority !== fPrio) return false;
    return true;
  });
  const toggle = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const addSelected = async () => {
    if (sel.size === 0) return;
    setBusy(true);
    try { await api.post(`/sprints/${sprintId}/tasks`, { task_ids: [...sel] }); toast.success(`${sel.size} tarefa(s) adicionada(s).`); setSel(new Set()); onChanged?.(); await loadEligible(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-slate-100 pt-4" data-testid="sprint-add-tasks">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="sprint-add-tasks-open" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><Plus className="w-4 h-4" /> Adicionar tarefas à Sprint</button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-heading font-semibold text-slate-900">Adicionar tarefas à Sprint</h4>
            <button onClick={() => setOpen(false)} data-testid="sprint-add-tasks-close" className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar tarefa" data-testid="add-tasks-search" className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <select value={fClass} onChange={(e) => setFClass(e.target.value)} data-testid="add-tasks-class" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="">Classe</option><option value="E">E</option><option value="D">D</option><option value="O">O</option><option value="Q">Q</option>
            </select>
            <select value={fPrio} onChange={(e) => setFPrio(e.target.value)} data-testid="add-tasks-prio" className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
              <option value="">Prioridade</option>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {eligible === null ? <p className="text-sm text-slate-400">Carregando...</p>
            : filtered.length === 0 ? <p className="text-sm text-slate-400" data-testid="add-tasks-empty">Nenhuma tarefa elegível.</p>
              : (
                <ul className="max-h-64 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid="add-tasks-list">
                  {filtered.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 px-3 py-2" data-testid={`eligible-task-${t.id}`}>
                      <input type="checkbox" checked={sel.has(t.id)} onChange={() => toggle(t.id)} data-testid={`eligible-check-${t.id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                        <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap text-slate-500">
                          <span className={t.impediment_active ? "text-amber-700" : t.on_board ? "text-emerald-700" : ""}>{taskLocation(t)}</span>
                          {t.assignee_name && <span>· {t.assignee_name}</span>}
                          {t.story_points != null && <span>· {t.story_points} SP</span>}
                          {t.impediment_active && <span className="inline-flex items-center gap-0.5 text-amber-700"><AlertTriangle className="w-3 h-3" /> impedida</span>}
                        </div>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${CLASS_STYLE[t.classification]}`}>{t.classification}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${PRIO_STYLE[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
                    </li>
                  ))}
                </ul>
              )}
          <div className="flex justify-end">
            <button onClick={addSelected} disabled={sel.size === 0 || busy} data-testid="add-tasks-confirm" className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50"><Plus className="w-4 h-4" /> Adicionar {sel.size > 0 ? `(${sel.size})` : ""}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Complete sprint modal (full closure) ----
function CompleteSprintModal({ sprintId, onClose, onDone }) {
  const [tasks, setTasks] = useState(null);
  const [review, setReview] = useState({ delivered: "", not_delivered: "", results: "" });
  const [retro, setRetro] = useState({ went_well: "", went_wrong: "", learnings: "" });
  const [improvements, setImprovements] = useState([]); // [{text}]
  const [impText, setImpText] = useState("");
  const [decisions, setDecisions] = useState({}); // task_id -> {decision, reason}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/sprints/${sprintId}`).then(({ data }) => setTasks(data.tasks || [])).catch((e) => toast.error(formatApiError(e)));
  }, [sprintId]);

  const incomplete = (tasks || []).filter((t) => t.status !== "cancelled" && (t.status !== "done" || (t.checklist_pending || 0) > 0));
  const setDec = (id, patch) => setDecisions((prev) => ({ ...prev, [id]: { ...(prev[id] || { decision: "", reason: "" }), ...patch } }));
  const addImprovement = () => { const t = impText.trim(); if (!t) return; setImprovements((p) => [...p, { text: t }]); setImpText(""); };
  const removeImprovement = (i) => setImprovements((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    for (const t of incomplete) {
      const d = decisions[t.id];
      if (!d || !d.decision || !(d.reason || "").trim()) {
        toast.error(`Defina decisão e motivo para "${t.title}".`); return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/sprints/${sprintId}/complete`, {
        review, retrospective: retro,
        improvements: improvements.map((i) => ({ text: i.text, create_task: true })),
        incomplete_decisions: incomplete.map((t) => ({ task_id: t.id, decision: decisions[t.id].decision, reason: decisions[t.id].reason.trim() })),
      });
      toast.success("Sprint concluída."); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="sprint-complete-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-2xl my-8 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <h3 className="font-heading text-xl font-semibold">Concluir sprint</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-6 overflow-y-auto">
          {tasks === null ? <p className="text-sm text-slate-400">Carregando...</p> : (
            <>
              <Section title="Revisão">
                <Area label="O que foi entregue" testid="review-delivered" value={review.delivered} onChange={(v) => setReview({ ...review, delivered: v })} />
                <Area label="O que não foi entregue" testid="review-not-delivered" value={review.not_delivered} onChange={(v) => setReview({ ...review, not_delivered: v })} />
                <Area label="Resultados" testid="review-results" value={review.results} onChange={(v) => setReview({ ...review, results: v })} />
              </Section>

              <Section title="Retrospectiva">
                <Area label="O que funcionou bem" testid="retro-well" value={retro.went_well} onChange={(v) => setRetro({ ...retro, went_well: v })} />
                <Area label="O que não funcionou" testid="retro-wrong" value={retro.went_wrong} onChange={(v) => setRetro({ ...retro, went_wrong: v })} />
                <Area label="Aprendizados" testid="retro-learnings" value={retro.learnings} onChange={(v) => setRetro({ ...retro, learnings: v })} />
              </Section>

              <Section title="Ações de melhoria">
                <p className="text-xs text-slate-500">Cada ação adicionada cria uma nova tarefa no Backlog do projeto (sem vínculo com esta sprint).</p>
                <div className="flex items-center gap-2">
                  <input value={impText} onChange={(e) => setImpText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImprovement(); } }} placeholder="Descreva a ação de melhoria" data-testid="improvement-input" className="flex-1 h-9 rounded-lg border border-slate-200 px-3 text-sm" />
                  <button type="button" onClick={addImprovement} data-testid="improvement-add" className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm"><Plus className="w-4 h-4" /> Adicionar tarefa</button>
                </div>
                {improvements.length > 0 && (
                  <ul className="space-y-1.5" data-testid="improvement-list">
                    {improvements.map((i, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`improvement-item-${idx}`}>
                        <Plus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="flex-1 min-w-0">{i.text}</span>
                        <button type="button" onClick={() => removeImprovement(idx)} className="text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={`Tarefas incompletas (${incomplete.length})`}>
                {incomplete.length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="no-incomplete">Todas as tarefas foram concluídas.</p>
                ) : (
                  <ul className="space-y-3" data-testid="incomplete-list">
                    {incomplete.map((t) => (
                      <li key={t.id} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`incomplete-${t.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900 flex-1 min-w-0 truncate">{t.title}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${CLASS_STYLE[t.classification]}`}>{t.classification}</span>
                          {(t.checklist_pending || 0) > 0 && <span className="text-xs text-amber-700">checklist pendente</span>}
                        </div>
                        <select value={(decisions[t.id] || {}).decision || ""} onChange={(e) => setDec(t.id, { decision: e.target.value })} data-testid={`decision-${t.id}`} className="w-full h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
                          <option value="">Selecione uma decisão...</option>
                          {DECISION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <input value={(decisions[t.id] || {}).reason || ""} onChange={(e) => setDec(t.id, { reason: e.target.value })} placeholder="Motivo (obrigatório)" data-testid={`reason-${t.id}`} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm" />
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Voltar</button>
          <button type="submit" disabled={busy || tasks === null} data-testid="complete-confirm" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">{busy ? "Concluindo..." : "Concluir sprint"}</button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</h4>
    {children}
  </div>
);
const Area = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs text-slate-500 mb-1">{label}</label>
    <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
  </div>
);

// ---- Permanent closure summary ----
function ClosureSummary({ closure }) {
  const r = closure.review || {}; const rt = closure.retrospective || {};
  return (
    <div className="border-t border-slate-100 pt-4 space-y-4" data-testid="sprint-closure-summary">
      <h4 className="font-heading font-semibold text-slate-900">Fechamento da sprint</h4>
      <Block title="Revisão" rows={[["Entregue", r.delivered], ["Não entregue", r.not_delivered], ["Resultados", r.results]]} />
      <Block title="Retrospectiva" rows={[["Funcionou bem", rt.went_well], ["Não funcionou", rt.went_wrong], ["Aprendizados", rt.learnings]]} />
      {(closure.improvements || []).length > 0 && (
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Ações de melhoria</h5>
          <ul className="space-y-1 text-sm" data-testid="closure-improvements">
            {closure.improvements.map((i, idx) => (
              <li key={idx} className="flex items-center gap-2"><Plus className="w-3.5 h-3.5 text-slate-400" /> {i.text} {i.task_id && <span className="text-xs text-emerald-700">(tarefa criada)</span>}</li>
            ))}
          </ul>
        </div>
      )}
      {(closure.delivered || []).length > 0 && (
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Tarefas entregues ({closure.delivered.length})</h5>
          <ul className="space-y-0.5 text-sm text-slate-700" data-testid="closure-delivered">{closure.delivered.map((d) => <li key={d.task_id}>• {d.title}</li>)}</ul>
        </div>
      )}
      {(closure.not_delivered || []).length > 0 && (
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Tarefas não entregues ({closure.not_delivered.length})</h5>
          <ul className="space-y-1 text-sm" data-testid="closure-not-delivered">
            {closure.not_delivered.map((d) => (
              <li key={d.task_id} className="text-slate-700">• {d.title} — <span className="text-slate-500">{DECISION_OPTIONS.find((o) => o.value === d.decision)?.label || d.decision}: {d.reason}</span></li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-slate-400">Concluída por {closure.closed_by_name || "—"} em {closure.closed_at ? new Date(closure.closed_at).toLocaleString("pt-BR") : "—"}.</p>
    </div>
  );
}
const Block = ({ title, rows }) => (
  <div>
    <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{title}</h5>
    <dl className="space-y-1 text-sm">
      {rows.map(([k, v]) => <div key={k}><dt className="text-slate-400 text-xs">{k}</dt><dd className="text-slate-700 whitespace-pre-wrap">{v || "—"}</dd></div>)}
    </dl>
  </div>
);
